import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import BlockIcon from "@mui/icons-material/Block";
import PaymentIcon from "@mui/icons-material/Payment";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import Popup from "../../../components/Popup";
import OnekhusaPaymentDialog from "../../../components/OnekhusaPaymentDialog";
import { ENABLE_FEE_ONLINE_PAYMENT } from "../../../config/featureFlags";
import { downloadReceiptPdf } from "../../../utils/feeAidApi";
import {
    FinancePageContainer,
    FinancePanel,
    FinanceHeadCell,
    FinanceTableRow,
    financeTableContainerSx,
} from "./financeUi";
import { getApiBaseUrl } from "../../../config/api";
import { getSchoolIdForUser } from "../../../utils/userContext";
import {
    getFeeRecordById,
    getStudentFeeRecords,
    recordPayment,
    voidPayment,
    applyWaiver,
} from "../../../redux/feeRelated/feeHandle";
import { underFeeControl } from "../../../redux/feeRelated/feeSlice";

const PAYMENT_METHODS = [
    "Cash",
    "Airtel Money",
    "TNM Mpamba",
    "Bank Transfer",
    "Cheque",
    "Other",
];

const formatMWK = (n) => "MWK " + Number(n || 0).toLocaleString();

function csvEscape(val) {
    const s = String(val ?? "");
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
}

function downloadCsv(filename, rows) {
    const csv = rows.join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function fmtDMY(d) {
    if (!d) return "—";
    const x = new Date(d);
    if (Number.isNaN(x.getTime())) return "—";
    return x.toLocaleDateString("en-GB");
}

function statusChipColor(status) {
    const s = String(status || "");
    if (s === "Unpaid") return "error";
    if (s === "Partial") return "warning";
    if (s === "Paid") return "success";
    if (s === "Waived") return "info";
    if (s === "Overpaid") return "secondary";
    return "default";
}

function feeErrorMessage(err) {
    if (!err) return "Request failed";
    if (typeof err === "string") return err;
    if (typeof err.response?.data?.message === "string") return err.response.data.message;
    if (err.message) return err.message;
    return "Request failed";
}

const FeeRecordDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { selectedFeeRecord: record, loading, statestatus, response, error } = useSelector(
        (s) => s.fee
    );

    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const [payOpen, setPayOpen] = useState(false);
    const [payAmount, setPayAmount] = useState("");
    const [payMethod, setPayMethod] = useState("Cash");
    const [payDate, setPayDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [payRef, setPayRef] = useState("");
    const [payReceivedBy, setPayReceivedBy] = useState(currentUser?.name || "");
    const [payNotes, setPayNotes] = useState("");

    const [waiverOpen, setWaiverOpen] = useState(false);
    const [waiverAmount, setWaiverAmount] = useState("");
    const [waiverReason, setWaiverReason] = useState("");

    const [voidOpen, setVoidOpen] = useState(false);
    const [voidReceiptNumber, setVoidReceiptNumber] = useState("");
    const [voidReason, setVoidReason] = useState("");

    const [receiptOpen, setReceiptOpen] = useState(false);
    const [receiptLoading, setReceiptLoading] = useState(false);
    const [receiptData, setReceiptData] = useState(null);
    const [receiptPdfLoading, setReceiptPdfLoading] = useState(false);

    const [onekhusaDialog, setOnekhusaDialog] = useState(false);
    const [selectedFeeRecord, setSelectedFeeRecord] = useState(null);

    const schoolId = getSchoolIdForUser(currentUser);

    const closeAllDialogs = useCallback(() => {
        setPayOpen(false);
        setWaiverOpen(false);
        setVoidOpen(false);
        setReceiptOpen(false);
        setVoidReceiptNumber("");
        setVoidReason("");
        setReceiptData(null);
    }, []);

    useEffect(() => {
        if (!id) return;
        dispatch(getFeeRecordById(id));
    }, [id, dispatch]);

    useEffect(() => {
        setPayReceivedBy((prev) => (currentUser?.name ? currentUser.name : prev));
    }, [currentUser?.name]);

    useEffect(() => {
        if (statestatus === "added") {
            setMessage("Completed successfully!");
            setShowPopup(true);
            dispatch(underFeeControl());
            if (id) dispatch(getFeeRecordById(id));
            closeAllDialogs();
        } else if (statestatus === "failed") {
            const msg =
                typeof response === "string" && response
                    ? response
                    : feeErrorMessage(error);
            setMessage(msg || "Request failed");
            setShowPopup(true);
            dispatch(underFeeControl());
        }
    }, [statestatus, response, error, dispatch, id, closeAllDialogs]);

    const totalDue = Number(record?.totalDue) || 0;
    const totalPaid = Number(record?.totalPaid) || 0;
    const balance = Number(record?.balance) || 0;
    const waiverAmt = Number(record?.waiverAmount) || 0;
    const status = record?.status;

    const paidRatio = totalDue > 0 ? Math.min((totalPaid / totalDue) * 100, 100) : 0;
    const progressColor =
        paidRatio >= 100 ? "success" : paidRatio >= 50 ? "warning" : "error";

    const payments = useMemo(
        () => (Array.isArray(record?.payments) ? record.payments : []),
        [record?.payments]
    );
    const activePayments = useMemo(
        () => payments.filter((p) => !p.isVoided),
        [payments]
    );
    const voidedPayments = useMemo(
        () => payments.filter((p) => p.isVoided),
        [payments]
    );

    const exportReceiptCsv = () => {
        if (!receiptData?.receiptNumber) return;
        const rows = [
            ["field", "value"].join(","),
            ...Object.entries(receiptData).map(([k, v]) => [csvEscape(k), csvEscape(v)].join(",")),
        ];
        downloadCsv(`receipt-${receiptData.receiptNumber}.csv`, rows);
    };

    const exportLedgerCsv = () => {
        if (!record || !id) return;
        const t = record?.feeStructure?.term ?? record?.term ?? "";
        const y = record?.feeStructure?.academicYear ?? record?.academicYear ?? "";
        const head = [
            [
                "section",
                "feeRecordId",
                "studentName",
                "rollNum",
                "className",
                "term",
                "academicYear",
                "totalDue",
                "totalPaid",
                "balance",
                "status",
                "waiverAmount",
                "waiverReason",
            ].join(","),
        ];
        const main = [
            [
                "summary",
                csvEscape(id),
                csvEscape(record.student?.name),
                csvEscape(record.student?.rollNum),
                csvEscape(record.sclass?.sclassName),
                csvEscape(t),
                csvEscape(y),
                csvEscape(record.totalDue),
                csvEscape(record.totalPaid),
                csvEscape(record.balance),
                csvEscape(record.status),
                csvEscape(record.waiverAmount),
                csvEscape(record.waiverReason),
            ].join(","),
        ];
        const payHead = [
            [
                "section",
                "receiptNumber",
                "paymentDate",
                "paymentMethod",
                "amount",
                "isVoided",
                "referenceNumber",
                "collectorName",
                "notes",
            ].join(","),
        ];
        const payLines = payments.map((p) =>
            [
                "payment",
                csvEscape(p.receiptNumber),
                csvEscape(p.paymentDate),
                csvEscape(p.paymentMethod),
                csvEscape(p.amount),
                csvEscape(p.isVoided ? "yes" : "no"),
                csvEscape(p.referenceNumber),
                csvEscape(p.collectorName),
                csvEscape(p.notes),
            ].join(",")
        );
        downloadCsv(`fee-ledger-${id.slice(0, 8)}.csv`, [...head, ...main, "", ...payHead, ...payLines]);
    };

    const openPayment = () => {
        const bal = balance > 0 ? balance : "";
        setPayAmount(bal === "" ? "" : String(bal));
        setPayMethod("Cash");
        setPayDate(new Date().toISOString().slice(0, 10));
        setPayRef("");
        setPayReceivedBy(currentUser?.name || "");
        setPayNotes("");
        setPayOpen(true);
    };

    const submitPayment = () => {
        const amt = Number(payAmount);
        if (!id || !Number.isFinite(amt) || amt <= 0) return;
        dispatch(
            recordPayment(id, {
                amount: amt,
                paymentMethod: payMethod,
                paymentDate: payDate,
                referenceNumber: payRef,
                collectorName: payReceivedBy,
                collectedById: currentUser._id,
                notes: payNotes,
                schoolId,
            })
        );
    };

    const openWaiver = () => {
        setWaiverAmount("");
        setWaiverReason("");
        setWaiverOpen(true);
    };

    const submitWaiver = () => {
        const wa = Number(waiverAmount);
        if (!id || !Number.isFinite(wa) || !String(waiverReason || "").trim()) return;
        dispatch(
            applyWaiver(id, {
                waiverAmount: wa,
                waiverReason: String(waiverReason).trim(),
            })
        );
    };

    const openVoid = (receiptNumber) => {
        setVoidReceiptNumber(receiptNumber);
        setVoidReason("");
        setVoidOpen(true);
    };

    const submitVoid = () => {
        if (!id || !voidReceiptNumber || !String(voidReason || "").trim()) return;
        dispatch(
            voidPayment(id, voidReceiptNumber, {
                voidReason: String(voidReason).trim(),
                voidedById: currentUser._id,
            })
        );
    };

    const openReceipt = async (receiptNumber) => {
        if (!id) return;
        setReceiptData(null);
        setReceiptOpen(true);
        setReceiptLoading(true);
        try {
            const res = await axios.get(
                `${getApiBaseUrl()}/FeeRecord/${id}/receipt/${receiptNumber}`
            );
            if (res.data?.message) {
                setReceiptData(null);
                setMessage(res.data.message);
                setShowPopup(true);
                setReceiptOpen(false);
            } else {
                setReceiptData(res.data);
            }
        } catch (e) {
            setMessage(feeErrorMessage(e));
            setShowPopup(true);
            setReceiptOpen(false);
        } finally {
            setReceiptLoading(false);
        }
    };

    const term = record?.feeStructure?.term ?? record?.term ?? "—";
    const year = record?.feeStructure?.academicYear ?? record?.academicYear ?? "—";
    const feeItems = record?.feeStructure?.feeItems;
    const canPay = status !== "Paid" && status !== "Waived";

    const balanceColor =
        balance > 0 ? "error.main" : balance === 0 ? "success.main" : "secondary.main";

    if (!record) {
        if (loading) {
        return (
            <FinancePageContainer maxWidth="md" className="no-print">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={360}>
                    <CircularProgress />
                </Box>
            </FinancePageContainer>
        );
        }
        return (
            <FinancePageContainer maxWidth="md">
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }} className="no-print">
                    <IconButton onClick={() => navigate(-1)} aria-label="Back" className="no-print">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography sx={{ mt: 2 }} color="text.secondary">
                        Fee record could not be loaded.
                    </Typography>
                </Paper>
            </FinancePageContainer>
        );
    }

    return (
        <FinancePageContainer maxWidth="md">
            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />

            <Paper
                className="no-print"
                elevation={0}
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    mb: 2.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: (theme) => theme.shadows[2],
                    background: (theme) =>
                        `linear-gradient(120deg, ${alpha(theme.palette.primary.main, 0.09)} 0%, ${alpha(
                            theme.palette.background.paper,
                            1
                        )} 55%)`,
                }}
            >
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start" flex={1} sx={{ minWidth: 0 }}>
                        <IconButton onClick={() => navigate(-1)} aria-label="Back" sx={{ mt: -0.25 }}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                                <Typography variant="h5" component="h1" fontWeight={800} sx={{ letterSpacing: "-0.02em" }}>
                                    {record.student?.name ?? "Student"}
                                </Typography>
                                <Chip
                                    size="small"
                                    label={status ?? "—"}
                                    color={statusChipColor(status)}
                                    variant="filled"
                                    sx={{ fontWeight: 700 }}
                                />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.5 }}>
                                Roll {record.student?.rollNum ?? "—"} · {record.sclass?.sclassName ?? "—"} · {term} ·{" "}
                                {year}
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ flexShrink: 0 }}>
                        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportLedgerCsv} size="small">
                            Ledger CSV
                        </Button>
                        {canPay && (
                            <>
                                {ENABLE_FEE_ONLINE_PAYMENT ? (
                                    <Button
                                        variant="outlined"
                                        startIcon={<PhoneAndroidIcon />}
                                        onClick={() => {
                                            setSelectedFeeRecord(record);
                                            setOnekhusaDialog(true);
                                        }}
                                        sx={{ borderColor: "#e8c97e", color: "#e8c97e" }}
                                        size="small"
                                    >
                                        Pay via Mobile Money
                                    </Button>
                                ) : null}
                                <Button variant="contained" startIcon={<PaymentIcon />} onClick={openPayment} size="small">
                                    Record payment
                                </Button>
                            </>
                        )}
                    </Stack>
                </Stack>
            </Paper>

            <FinancePanel title="Balance summary" sx={{ mb: 2 }} contentSx={{ p: { xs: 2, sm: 2.5 } }} className="no-print">
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            Total due
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 800 }}>
                            {formatMWK(totalDue)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            Total paid
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 800, color: "success.main" }}>
                            {formatMWK(totalPaid)}
                        </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            Balance
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 800, color: balanceColor }}>
                            {formatMWK(balance)}
                        </Typography>
                    </Grid>
                    {waiverAmt > 0 && (
                        <Grid item xs={6} md={3}>
                            <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                Waiver
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 800, color: "info.main" }}>
                                {formatMWK(waiverAmt)}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ flex: 1 }}>
                        <LinearProgress
                            variant="determinate"
                            value={paidRatio}
                            color={progressColor}
                            sx={{ height: 10, borderRadius: 99 }}
                        />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 44, fontWeight: 700 }}>
                        {Math.round(paidRatio)}%
                    </Typography>
                </Stack>
            </FinancePanel>

            {feeItems?.length > 0 && (
                <FinancePanel title="Fee breakdown" sx={{ mb: 2 }} contentSx={{ p: 0 }} className="no-print">
                    <TableContainer sx={financeTableContainerSx}>
                        <Table size="medium">
                            <TableHead>
                                <TableRow>
                                    <FinanceHeadCell>Fee type</FinanceHeadCell>
                                    <FinanceHeadCell>Description</FinanceHeadCell>
                                    <FinanceHeadCell align="right">Amount</FinanceHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {feeItems.map((it) => {
                                    const iid = it.id || it._id || it.feeType;
                                    return (
                                        <FinanceTableRow key={iid}>
                                            <TableCell>
                                                <Chip
                                                    size="small"
                                                    label={it.feeType || "—"}
                                                    variant="outlined"
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>{it.description || "—"}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                                                {formatMWK(it.amount)}
                                            </TableCell>
                                        </FinanceTableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </FinancePanel>
            )}

            <FinancePanel
                className="no-print"
                title={`Payment history (${activePayments.length})`}
                action={
                    canPay ? (
                        <Button size="small" variant="outlined" onClick={openWaiver}>
                            Apply waiver
                        </Button>
                    ) : null
                }
                sx={{ mb: 2 }}
                contentSx={{ p: 0 }}
            >
                {activePayments.length === 0 ? (
                    <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
                        <Typography color="text.secondary">No payments recorded yet.</Typography>
                    </Box>
                ) : (
                    <TableContainer sx={financeTableContainerSx}>
                        <Table size="medium">
                            <TableHead>
                                <TableRow>
                                    <FinanceHeadCell>Receipt</FinanceHeadCell>
                                    <FinanceHeadCell>Date</FinanceHeadCell>
                                    <FinanceHeadCell>Method</FinanceHeadCell>
                                    <FinanceHeadCell>Ref</FinanceHeadCell>
                                    <FinanceHeadCell>Received by</FinanceHeadCell>
                                    <FinanceHeadCell align="right">Amount</FinanceHeadCell>
                                    <FinanceHeadCell align="right">Actions</FinanceHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {activePayments.map((p) => {
                                    const pid = p.id || p._id;
                                    return (
                                        <FinanceTableRow key={pid}>
                                            <TableCell sx={{ fontWeight: 700, color: "primary.main" }}>
                                                {p.receiptNumber}
                                            </TableCell>
                                            <TableCell>{fmtDMY(p.paymentDate)}</TableCell>
                                            <TableCell>
                                                <Chip size="small" label={p.paymentMethod || "—"} />
                                            </TableCell>
                                            <TableCell>{p.referenceNumber || "—"}</TableCell>
                                            <TableCell>{p.collectorName || "—"}</TableCell>
                                            <TableCell
                                                align="right"
                                                sx={{ color: "success.main", fontWeight: 800, fontVariantNumeric: "tabular-nums" }}
                                            >
                                                {formatMWK(p.amount)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Receipt">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => openReceipt(p.receiptNumber)}
                                                    >
                                                        <PrintIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Void payment">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => openVoid(p.receiptNumber)}
                                                    >
                                                        <BlockIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </FinanceTableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {voidedPayments.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 2, bgcolor: "grey.50" }}>
                        {voidedPayments.length} voided payment{voidedPayments.length === 1 ? "" : "s"} — excluded from
                        totals
                    </Typography>
                )}
            </FinancePanel>

            <Dialog open={payOpen} onClose={() => setPayOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Record payment</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 0.5 }}>
                        <Alert severity="info">
                            Current balance: <strong>{formatMWK(balance)}</strong>
                        </Alert>
                        <TextField
                            required
                            fullWidth
                            size="small"
                            label="Amount"
                            type="number"
                            value={payAmount}
                            onChange={(e) => setPayAmount(e.target.value)}
                            inputProps={{ min: 0, step: "0.01" }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">MWK</InputAdornment>
                                ),
                            }}
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel>Payment method</InputLabel>
                            <Select
                                label="Payment method"
                                value={payMethod}
                                onChange={(e) => setPayMethod(e.target.value)}
                            >
                                {PAYMENT_METHODS.map((m) => (
                                    <MenuItem key={m} value={m}>
                                        {m}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            size="small"
                            label="Payment date"
                            type="date"
                            value={payDate}
                            onChange={(e) => setPayDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Reference number"
                            value={payRef}
                            onChange={(e) => setPayRef(e.target.value)}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Received by"
                            value={payReceivedBy}
                            onChange={(e) => setPayReceivedBy(e.target.value)}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Notes"
                            value={payNotes}
                            onChange={(e) => setPayNotes(e.target.value)}
                            multiline
                            minRows={2}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPayOpen(false)}>Cancel</Button>
                    {ENABLE_FEE_ONLINE_PAYMENT ? (
                        <Button
                            variant="outlined"
                            startIcon={<PhoneAndroidIcon />}
                            onClick={() => {
                                setSelectedFeeRecord(record);
                                setPayOpen(false);
                                setOnekhusaDialog(true);
                            }}
                            sx={{ borderColor: "#e8c97e", color: "#e8c97e", ml: 1 }}
                        >
                            Pay via Mobile Money
                        </Button>
                    ) : null}
                    <Button
                        variant="contained"
                        startIcon={<PaymentIcon />}
                        onClick={submitPayment}
                        disabled={loading || !Number(payAmount) || Number(payAmount) <= 0}
                    >
                        Record Payment
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={waiverOpen} onClose={() => setWaiverOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Apply waiver</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 0.5 }}>
                        <Alert severity="info">
                            Total due: <strong>{formatMWK(totalDue)}</strong>
                            {" · "}
                            Current waiver: <strong>{formatMWK(waiverAmt)}</strong>
                        </Alert>
                        <TextField
                            required
                            fullWidth
                            size="small"
                            label="Waiver amount"
                            type="number"
                            value={waiverAmount}
                            onChange={(e) => setWaiverAmount(e.target.value)}
                            inputProps={{ min: 0, max: totalDue, step: "0.01" }}
                            helperText={`Maximum: ${formatMWK(totalDue)}`}
                        />
                        <TextField
                            required
                            fullWidth
                            size="small"
                            label="Reason"
                            value={waiverReason}
                            onChange={(e) => setWaiverReason(e.target.value)}
                            multiline
                            minRows={3}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWaiverOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={submitWaiver}
                        disabled={
                            loading ||
                            !String(waiverReason || "").trim() ||
                            !Number(waiverAmount) ||
                            Number(waiverAmount) < 0 ||
                            Number(waiverAmount) > totalDue
                        }
                    >
                        Apply Waiver
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={voidOpen} onClose={() => setVoidOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Void payment</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ pt: 0.5 }}>
                        <Alert severity="warning">
                            Voiding cannot be undone. The student&apos;s balance will increase.
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                            Receipt: <strong>{voidReceiptNumber}</strong>
                        </Typography>
                        <TextField
                            required
                            fullWidth
                            label="Reason"
                            value={voidReason}
                            onChange={(e) => setVoidReason(e.target.value)}
                            multiline
                            minRows={3}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVoidOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={submitVoid}
                        disabled={loading || !String(voidReason || "").trim()}
                    >
                        Void payment
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={receiptOpen} onClose={() => setReceiptOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle className="no-print">Receipt preview</DialogTitle>
                <DialogContent dividers>
                    {receiptLoading ? (
                        <Box display="flex" justifyContent="center" py={4} className="no-print">
                            <CircularProgress size={32} />
                        </Box>
                    ) : receiptData ? (
                        <Box className="print-area" sx={{ py: 1, px: { xs: 0, sm: 1 } }}>
                            <Typography className="print-title" align="center" fontWeight={800} variant="h5" sx={{ letterSpacing: 1 }}>
                                {receiptData.schoolName || currentUser?.schoolName || "School"}
                            </Typography>
                            <Typography align="center" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                School fees office · Official receipt
                            </Typography>
                            <Typography align="center" variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                                {receiptData.schoolAddress || currentUser?.schoolAddress || ""}
                            </Typography>
                            <Divider sx={{ my: 2, borderColor: "#000" }} />
                            <Grid container spacing={1} sx={{ mb: 2 }}>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Receipt No:</strong>
                                    </Typography>
                                    <Typography variant="body1" fontWeight={700}>
                                        {receiptData.receiptNumber}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sx={{ textAlign: "right" }}>
                                    <Typography variant="body2">
                                        <strong>Date:</strong>
                                    </Typography>
                                    <Typography variant="body1">{fmtDMY(receiptData.paymentDate)}</Typography>
                                </Grid>
                            </Grid>
                            <Stack spacing={0.75} sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Student:</strong> {receiptData.studentName}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Roll No:</strong> {receiptData.rollNum ?? "—"}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Class:</strong> {receiptData.className}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Term / Year:</strong> {receiptData.term} · {receiptData.academicYear}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Payment method:</strong> {receiptData.paymentMethod}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Reference:</strong> {receiptData.referenceNumber || "—"}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Cashier:</strong> {receiptData.collectorName || currentUser?.name || "—"}
                                </Typography>
                            </Stack>
                            <Box
                                sx={{
                                    border: "2px solid #000",
                                    py: 2,
                                    px: 2,
                                    textAlign: "center",
                                    mb: 2,
                                }}
                            >
                                <Typography variant="body2" fontWeight={600}>
                                    Amount received
                                </Typography>
                                <Typography variant="h4" fontWeight={800}>
                                    {formatMWK(receiptData.amountPaid)}
                                </Typography>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Total due:</strong> {formatMWK(receiptData.totalDue)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} sx={{ textAlign: "right" }}>
                                    <Typography variant="body2">
                                        <strong>Balance after:</strong> {formatMWK(receiptData.balance)}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Box
                                sx={{
                                    width: 110,
                                    height: 110,
                                    border: "2px dashed #666",
                                    borderRadius: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    textAlign: "center",
                                    mx: "auto",
                                    mt: 3,
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                                    Official stamp
                                </Typography>
                            </Box>
                            <Typography variant="caption" display="block" align="center" sx={{ mt: 2 }}>
                                Thank you for your payment. Please retain this receipt.
                            </Typography>
                        </Box>
                    ) : (
                        <Typography color="text.secondary" className="no-print">
                            No receipt data.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions className="no-print">
                    <Button onClick={() => setReceiptOpen(false)}>Close</Button>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportReceiptCsv}
                        disabled={!receiptData}
                    >
                        Export CSV
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={async () => {
                            if (!receiptData?.receiptNumber || !id) return;
                            setReceiptPdfLoading(true);
                            try {
                                await downloadReceiptPdf(id, receiptData.receiptNumber);
                            } finally {
                                setReceiptPdfLoading(false);
                            }
                        }}
                        disabled={!receiptData || receiptPdfLoading}
                    >
                        {receiptPdfLoading ? "PDF…" : "Download PDF"}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        onClick={() => window.print()}
                        disabled={!receiptData}
                    >
                        Print Receipt
                    </Button>
                </DialogActions>
            </Dialog>

            {ENABLE_FEE_ONLINE_PAYMENT ? (
                <OnekhusaPaymentDialog
                    open={onekhusaDialog}
                    onClose={() => setOnekhusaDialog(false)}
                    feeRecordId={selectedFeeRecord?._id || selectedFeeRecord?.id || id}
                    studentName={selectedFeeRecord?.student?.name || record?.student?.name || ""}
                    outstandingBalance={Number(selectedFeeRecord?.balance ?? record?.balance) || 0}
                    schoolId={schoolId || currentUser?.id || currentUser?._id}
                    onPaymentConfirmed={() => {
                        setOnekhusaDialog(false);
                        if (id) dispatch(getFeeRecordById(id));
                        const sid = selectedFeeRecord?.studentId || record?.studentId;
                        if (sid) dispatch(getStudentFeeRecords(sid));
                    }}
                />
            ) : null}
        </FinancePageContainer>
    );
};

export default FeeRecordDetail;
